import { Box, Drawer, Popover, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { InfoOutlined } from '@mui/icons-material';
import { PatientServiceItem, PatientServiceItemType, PaymentTypeName } from '@uplift/api';
import { mobileBreakpoint, useIsMobile } from '@uplift/hooks';
import { AppointmentState } from '@uplift/types';
import React from 'react';


const useStyles = makeStyles()(theme => ({
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    paddingRight: '16px',
  },
  buttonIcon: {
    fontSize: '18px',
  },
  popover: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    color: 'initial',
    maxWidth: 340,
  },
  popoverTitleContainer: {
    height: '56px',
    display: 'flex',
    alignItems: 'center',
  },
  popoverTitle: {
    fontWeight: 700,
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '8px',
    fontSize: '14px',
  },
  popoverDescriptionTitle: {
    fontWeight: 700,
  },
}));


export type WhatDoTheseTermMeansProps = {
  serviceItem: PatientServiceItem;
};


type TermDetails = {
  title: string;
  description: string;
};


const descriptions = {
  descriptionOfService: {
    title: 'Description of service',
    description: 'shows the kind of care you received. This includes a medical code that’s used in the healthcare system, so payers and other providers understand your care.',
  },
  selfPay: {
    title: 'Self Pay',
    description: 'means that you’re not using insurance to pay for these sessions and are covering the full cost yourself.'
  },
  insuranceApplied: {
    title: 'Insurance applied',
    description: 'means you used your insurance coverage to pay for part or all of the services received.'
  },
  totalCharge: {
    title: 'Total charge',
    description: 'is the full amount billed by UpLift.'
  },
  allowableAmount: {
    title: 'Allowable amount',
    description: 'is the maximum amount that your health plan is willing to pay for this service.'
  },
  paidByInsurance: {
    title: 'Paid by insurance',
    description: 'is the amount your health plan covered.',
  },
  insuranceClaimProcessing: {
    title: 'Insurance claim processing',
    description: 'means that your insurance is currently reviewing the claim submitted for this service.',
  },
  canceled: {
    title: 'Canceled',
    description: 'means that your scheduled session was called off.',
  },
  lateCancellationFees: {
    title: 'Late cancellation fees',
    description: 'may be charged if you cancel a session less than 24 hours before it starts.',
  },
  noShowFees: {
    title: 'No show fees',
    description: 'may be charged if you don’t go to your scheduled session.Your provider will wait 10 minutes before marking a session as a “no show.”',
  },
};


export const WhatDoTheseTermMeans: React.VFC<WhatDoTheseTermMeansProps> = ({ serviceItem }) => {
  const [ open, setOpen ] = React.useState<boolean>(false);
  const isMobile = useIsMobile(mobileBreakpoint);
  const { classes } = useStyles();
  const anchorElRef = React.useRef<HTMLButtonElement>();
  const currentDescriptions = React.useMemo(() => {
    const result: TermDetails[] = [];

    if (serviceItem.service.cptCodes?.length) {
      result.push(descriptions.descriptionOfService);
    }

    if (serviceItem.payment) {
      if (serviceItem.payment.type === PaymentTypeName.cash) {
        result.push(descriptions.selfPay);
      } else if (serviceItem.payment.type === PaymentTypeName.insurance) {
        const { claim } = serviceItem.payment;

        result.push(descriptions.paidByInsurance);

        if (claim) {
          result.push(descriptions.allowableAmount);
          result.push(descriptions.totalCharge);
          result.push(descriptions.insuranceApplied);
        } else {
          result.push(descriptions.insuranceClaimProcessing);
        }
      }

    }

    if (serviceItem.serviceType === PatientServiceItemType.appointment) {
      const { appointment, clientDidNotShow } = serviceItem.service;

      if (appointment.state === AppointmentState.cancelled) {
        result.push(descriptions.canceled);
      }

      if (appointment.chargeFee) {
        if (clientDidNotShow) {
          result.push(descriptions.noShowFees);
        } else {
          result.push(descriptions.lateCancellationFees);
        }
      }
    }

    return result;
  }, [ serviceItem ]);

  const popoverContent = (
    <Box className={classes.popover}>
      <Box className={classes.popoverTitleContainer}>
        <Typography variant="body1" className={classes.popoverTitle}>
          What do these terms mean?
        </Typography>
      </Box>
      <Box className={classes.popoverContent}>
        {currentDescriptions.map(cd => (
          <Typography key={cd.title} variant="body2" color="textPrimary">
            <strong>{cd.title}</strong> {cd.description}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Typography
        variant="body2"
        color="primary"
        onClick={() => setOpen(!open)}
        ref={anchorElRef}
        className={classes.button}
      >
        <InfoOutlined className={classes.buttonIcon} />
        <span>What do these terms mean?</span>
      </Typography>
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchorElRef.current}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          onClose={() => setOpen(false)}
        >
          {popoverContent}
        </Popover>
      )}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setOpen(false)}
          slotProps={{
            paper: {
              elevation: 1,
              style: {
                maxWidth: 'calc(100% - 16px)',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                margin: '0 auto',
              }
            }
          }}
        >
          {popoverContent}
          <Box height="40px" />
        </Drawer>
      )}
    </>
  );
};
